import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'

const EMPAndTemplates = () => (
  <Page
    title="Event Medical Plan (EMP) and Templates"
    intro="The Event Medical Plan is generated as a PDF from the operational, clinical, and risk planning tabs. It moves from draft to approved, can be shared with staff and the client, and reusable templates speed up similar events."
  >
    <Section title="Generating the EMP">
      <Bullets>
        <Bullet>
          The <strong>Event Medical Plan</strong> card sits on the right of
          the event record. Tick or untick <strong>Include shifts</strong> and{' '}
          <strong>Include vehicles</strong> to choose whether the shift roster
          and booked vehicles print in the plan, then click{' '}
          <strong>Generate Medical Plan</strong> and{' '}
          <strong>Download Medical Plan</strong> to open the PDF.
        </Bullet>
        <Bullet>
          The document pulls together the operational plan, clinical
          considerations, receiving hospitals (with confidential contact
          numbers marked), Martyn's Law arrangements, escalation and OPELs,
          the risk assessment, mitigations, and method statements — empty
          sections are dropped automatically.
        </Bullet>
        <Bullet>
          Until approved, every page carries a{' '}
          <strong>DRAFT - NOT FOR CIRCULATION</strong> banner, and the
          document information block shows who printed it and the approval
          status.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Approving and sharing">
      <Step number="1" title="Approve the EMP">
        Once planning is complete, click <strong>Approve EMP</strong> and
        confirm. The card then shows who approved it and when, the draft
        banner disappears, and an <strong>EMP approved</strong> chip appears
        on the event.
      </Step>
      <Step number="2" title="Email it to staff">
        <strong>Email to Staff</strong> (in the card's three-dot menu, only
        available once approved) emails everyone assigned to a shift a link
        to the read-only event view, where they can see the plan and any
        staff-visible documents. In the AmbuNet mobile app, staff open the
        event from their shift and tap the approved plan to read it as
        sections on screen, so there is no PDF to download on a phone.
      </Step>
      <Step number="3" title="Share with the client if needed">
        The client sees the EMP in their client portal once it's approved.
        The <strong>EMP Visible to Client (including Draft)</strong> switch
        shares it earlier, while still in draft — this saves immediately.
      </Step>
      <Info title="Who can do this?">
        Approving the EMP needs the <strong>Event Planning</strong> team —
        the same access as editing the plan. There is no separate approver
        role, and no un-approve button, so approve deliberately.
      </Info>
      <Info title="EMP not needed for this event?">
        Use <strong>EMP not Required</strong> in the three-dot menu to mark
        the event as not needing a plan — the card shows "Not Required" and
        the event gets an <strong>EMP not required</strong> chip.
      </Info>
    </Section>

    <Section title="EMP templates">
      <Bullets>
        <Bullet>
          <strong>Save Current as Template</strong> stores this event's plan
          for reuse — give it a title and choose whether to include the
          operational plan, risk assessment, and medical considerations.
        </Bullet>
        <Bullet>
          <strong>Load Template</strong> fills the current event's plan from
          a saved template. Loaded content still needs{' '}
          <strong>Save Changes</strong> to be kept.
        </Bullet>
        <Bullet>
          Templates also power the inline wording suggestions on planning
          fields — recent templates feed the chips and the suggestions
          browser on fields like Description, Command &amp; Communications,
          and the major incident and overwhelm plans.
        </Bullet>
        <Bullet>
          View or delete saved templates under Site Configuration &gt; Events
          &gt; Medical Plans &gt; View EMP Templates. New templates can only
          be created from an event, not from the admin screen.
        </Bullet>
      </Bullets>
      <Warning title="Loading replaces, it doesn't merge">
        Loading a template overwrites the event's existing operational,
        clinical, and risk content with the template's — including clearing
        sections the template doesn't contain. Load templates at the start of
        planning, not after you've written event-specific detail.
      </Warning>
    </Section>
  </Page>
)

export default EMPAndTemplates
